"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useAuth } from "@/context/AuthContext";
import type { PerfumeProduct } from "@/services/catalogService";
import { Shield } from "lucide-react";
import { localProductImageFallback, productImageUrl } from "@/lib/productImageUrl";
import { formatAudPrice } from "@/lib/currency";

interface ProductTileProps {
  product: PerfumeProduct;
}

export const ProductTile: React.FC<ProductTileProps> = ({ product }) => {
  const router = useRouter();
  const { role } = useAuth();
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  // Setup click listener to close context menu on click outside
  useEffect(() => {
    const closeMenu = () => setContextMenu(null);
    window.addEventListener("click", closeMenu);
    return () => {
      window.removeEventListener("click", closeMenu);
    };
  }, []);

  const handleContextMenu = (e: React.MouseEvent) => {
    if (role === "administrator") {
      e.preventDefault();
      setContextMenu({ x: e.clientX, y: e.clientY });
    }
  };

  // Format imageUrl safely
  const mainImageName = product.images.main || "placeholder.svg";

  return (
    <>
      <Link
        href={`/product/${encodeURIComponent(product.sku)}`}
        className="product-tile-card"
        onContextMenu={handleContextMenu}
      >
        {/* Configurable image container */}
        <div className="product-tile-image-container">
          {/* Low Stock Indicator */}
          {product.stock === 0 ? (
            <div className="product-tile-badge product-tile-badge-sold-out">
              Sold Out
            </div>
          ) : product.stock <= 5 ? (
            <div className="product-tile-badge product-tile-badge-low-stock">
              Only {product.stock} Left
            </div>
          ) : null}

          <img
            src={productImageUrl(mainImageName)}
            alt={`${product.brand} ${product.name}`}
            loading="lazy"
            decoding="async"
            className="product-tile-image"
            onError={(e) => {
              // Fallback to local image in public folder, or SVG placeholder if missing
              const target = e.target as HTMLImageElement;
              const fallbackSrc = localProductImageFallback(mainImageName);
              if (!target.src.endsWith(fallbackSrc)) {
                target.src = fallbackSrc;
                return;
              }
              target.onerror = () => {
                // Final fallback to a pure CSS placeholder
                target.style.display = "none";
                const parent = target.parentElement;
                if (parent) {
                  const placeholder = document.createElement("div");
                  placeholder.className = "flex flex-col items-center justify-center text-center p-4 h-full w-full bg-neutral-100 dark:bg-neutral-800 text-foreground/40 font-mono text-[10px]";
                  placeholder.innerHTML = `<span>[${product.brand.toUpperCase()}]</span><span class="font-bold text-[11px] mt-1">${product.name.toUpperCase()}</span><span class="mt-4 text-[9px]">NO IMAGE AVAILABLE</span>`;
                  parent.appendChild(placeholder);
                }
              };
            }}
          />
        </div>

        {/* Product Information */}
        <div className="product-tile-info">
          <div className="flex justify-between items-start">
            <div>
              <p className="product-tile-brand">
                {product.brand}
              </p>
              <h3 className="product-tile-name">
                {product.name}
              </h3>
            </div>
            <p className="product-tile-price">
              {formatAudPrice(product.price)}
            </p>
          </div>

          {/* Olfactory Notes (Scents) */}
          <div className="product-tile-tags">
            {product.scents.slice(0, 3).map((note) => (
              <span
                key={note}
                className="product-tile-tag"
              >
                {note}
              </span>
            ))}
          </div>
        </div>
      </Link>

      {/* Admin Floating Context Menu */}
      {contextMenu && (
        <div
          className="fixed bg-background/95 backdrop-blur-sm border border-border p-1 shadow-2xl z-[9999] text-xs font-sans uppercase tracking-widest min-w-36 transition-all duration-100 animate-in fade-in zoom-in-95"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setContextMenu(null);
              router.push(`/admin?edit=${product.product_id}`);
            }}
            className="w-full text-left px-3 py-2 hover:bg-primary/15 hover:text-primary transition-all duration-200 font-bold cursor-pointer outline-none border-none text-[9px] flex items-center gap-1.5"
          >
            <Shield className="w-3.5 h-3.5 text-primary" />
            Edit Product
          </button>
        </div>
      )}
    </>
  );
};
export default ProductTile;
